<!-- @format -->

<template>
	<header class="header">
		<el-input
			v-model="filterText"
			class="res-list-header-search"
			placeholder="搜索"
			prefix-icon="Search"
			@keyup.enter="onSearch"
		/>
		<el-button type="primary" :disabled="!!attrs['form-disabled']" @click="onAdd()">新增</el-button>
	</header>

	<el-table :data="data" row-key="objectid" style="width: 100%">
		<el-table-column v-for="itm in item.children" :key="itm.code" :prop="itm.options.code" min-width="200">
			<template #header>
				<template v-if="itm.options.isKey === '1'">
					<svg-icon name="key" />
				</template>
				{{ itm.options.label }}
			</template>

			<template #default="scope">
				<template v-if="showComponentValueList.includes(getItem(scope.column.property)?.type)">
					<component
						:is="getWidgetName(getItem(scope.column.property)!.type)"
						:is-show-value="true"
						:model-value="scope.row[scope.column.property]"
						:options="getItem(scope.column.property)!.options"
					/>
				</template>
				<template v-else>
					{{ scope.row[scope.column.property] }}
				</template>
			</template>
		</el-table-column>

		<el-table-column fixed="right" label="设置" width="180">
			<template #default="scope">
				<el-button link type="primary" :disabled="!!attrs['form-disabled']" @click="onEdit(scope.row)">编辑</el-button>
				<el-button link type="primary" :disabled="!!attrs['form-disabled']" @click="onAdd(scope.row)">复制</el-button>
				<el-button link type="primary" :disabled="!!attrs['form-disabled']" @click="onDelete(scope.row)">
					删除
				</el-button>
			</template>
		</el-table-column>
	</el-table>

	<el-dialog v-model="dialogData.visible" title="新增" width="70%" @close="onClose">
		<el-form ref="formRef" :model="dialogData.data">
			<Form v-model="dialogData.data" :widget-datas="item.children" :show-label-icon="true"></Form>
		</el-form>

		<template #footer>
			<span class="dialog-footer">
				<el-button type="primary" @click="formValidate(formRef)">保存</el-button>
				<el-button @click="onClose">取消</el-button>
			</span>
		</template>
	</el-dialog>
</template>

<script setup lang="ts">
	import { uuid } from '@power/utils';
	import Form from '../index.vue';
	import { getDefaultValue } from '../../../utils';
	import { getWidgetName } from './index';
	import type { FormInstance } from 'element-plus';

	// 用组件展示对应列的数据列表
	const showComponentValueList = ['select', 'radio', 'checkbox'];

	// props
	interface Props {
		item: any;
		modelValue: any;
	}

	interface DialogData {
		type: 'add' | 'edit';
		data: Record<string, any>;
		visible: boolean;
	}
	const props = withDefaults(defineProps<Props>(), {});

	const emit = defineEmits(['update:modelValue', 'onFieldChange']);

	const attrs = useAttrs();

	const putId = (list) =>
		Array.isArray(list) ? list.map((itm) => ({ ...itm, objectid: uuid() })) : { ...list, objectid: uuid() };

	const formRef = ref<FormInstance>();

	const dialogData = ref<DialogData>({
		type: 'add',
		data: {},
		visible: false,
	});

	const filterText = ref('');

	const data = computed({
		get() {
			return props.modelValue;
		},
		set(value) {
			emit('update:modelValue', value);
		},
	});

	const formDatas = computed(() => dialogData.value.data);

	const formValidate = async (formEl: FormInstance | undefined) => {
		if (!formEl) return;
		await formEl.validate((vaild) => {
			if (vaild) {
				onSave();
			}
		});
	};

	const onSave = () => {
		// 记录change的值
		const onChange = attrs.onFieldChange as Function;
		const item = dialogData.value.data;

		if (dialogData.value.type === 'add') {
			data.value ? data.value.push(item) : (data.value = [item]);

			onChange && onChange('add', item, props.item);
		} else {
			const idx = data.value.findIndex((itm) => itm.objectid === item!.objectid);
			data.value.splice(idx, 1, item);

			onChange && onChange('edit', item, props.item);
		}
		onClose();
	};

	const onAdd = (item?: any) => {
		const defaultValue = () => getDefaultValue([props.item]);
		const data = putId(item ? item : defaultValue());
		dialogData.value = {
			type: 'add',
			visible: true,
			data,
		};
	};

	const onEdit = (item) => {
		dialogData.value = {
			type: 'edit',
			visible: true,
			data: item,
		};
	};

	const onDelete = (item) => {
		const idx = data.value.findIndex((itm) => itm.objectid === item.objectid);
		data.value.splice(idx, 1);

		// 记录change的值
		const onChange = attrs.onFieldChange as Function;
		onChange && onChange('del', item, props.item);
	};

	const onSearch = () => {};

	const getItem = (code) => {
		return props.item.children.find((itm) => itm.options.code === code);
	};

	const onClose = () => {
		dialogData.value.visible = false;
		dialogData.value.data = {};
	};

	const setFieldsValue = (fields) => {
		dialogData.value.data = { ...dialogData.value.data, ...fields };
	};

	provide('setFieldsValue', setFieldsValue);
	provide('widgetDatas', props.item.children);
	provide('formDatas', formDatas);
</script>

<style scoped lang="scss">
	.header {
		display: flex;
		justify-content: space-between;
		margin-bottom: 5px;

		.res-list-header-search {
			width: 300px;
		}
	}
</style>
